import i18n from '@/i18n';
import { useState, useEffect, useCallback } from 'react';

/**
 * Спільний стан шапки розділу «Бюджет».
 *
 * Операційні елементи (вкладка · місяць · сценарій цін) живуть у топбарі, а не
 * в тілі сторінки — §6 дизайн-контракту. Механізм той самий, що в
 * useMarketPricesUi / useForecastUi: модульне значення + подія синхронізації в
 * межах вкладки браузера, без контексту й без пропів через півдерева.
 */
const EV = 'helios:budget-ui-change';

export type BudgetTab = 'month' | 'year' | 'accuracy' | 'cash';
/** Сценарій цін річного бюджету; затверджується базовий (ТЗ A3.3). */
export type BudgetScenario = 'low' | 'base' | 'high';

const lt = <K,>(k: K, key: string): [K, string] => {
  const a = [k] as unknown as [K, string];
  Object.defineProperty(a, 1, { get: () => i18n.t(key), enumerable: true });
  return a;
};

export const BUDGET_TABS: [BudgetTab, string][] = [
  lt('month', 'ak_bud_month'),
  lt('year', 'ak_bud_year'),
  lt('accuracy', 'ak_bud_accuracy'),
  lt('cash', 'ak_bud_cash'),
];

export const BUDGET_SCENARIOS: [BudgetScenario, string][] = [
  lt('low', 'ak_bud_low'),
  lt('base', 'ak_bud_base'),
  lt('high', 'ak_bud_high'),
];

/** Поточний місяць у часі станції, 'YYYY-MM'. */
export function currentMonthIso(): string {
  const d = new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/Kyiv' }));
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** Етап регламенту: чернетка T−5 або фінальна версія T−1 (ТЗ A2). */
export type BudgetStage = 'draft' | 'final';
export const BUDGET_STAGES: [BudgetStage, string][] = [
  lt('draft', 'ak_bud_draft'),
  lt('final', 'ak_bud_final'),
];

const state = {
  tab: 'month' as BudgetTab,
  month: currentMonthIso(),
  scenario: 'base' as BudgetScenario,
  stage: 'final' as BudgetStage,
  // Хедер виконує дію (генерація, затвердження), тіло сторінки має
  // перечитати дані. Лічильник замість колбека: топбар не знає про сторінку.
  reloadToken: 0,
  busy: '' as string,
};

const emit = () => window.dispatchEvent(new CustomEvent(EV));

export function useBudgetUi() {
  const [, force] = useState(0);
  useEffect(() => {
    const refresh = () => force((x) => x + 1);
    window.addEventListener(EV, refresh);
    return () => window.removeEventListener(EV, refresh);
  }, []);

  const setTab = useCallback((t: BudgetTab) => {
    if (state.tab !== t) { state.tab = t; emit(); }
  }, []);
  const setMonth = useCallback((m: string) => {
    if (m && state.month !== m) { state.month = m; emit(); }
  }, []);
  const setScenario = useCallback((s: BudgetScenario) => {
    if (state.scenario !== s) { state.scenario = s; emit(); }
  }, []);

  const setStage = useCallback((st: BudgetStage) => {
    if (state.stage !== st) { state.stage = st; emit(); }
  }, []);
  const setBusy = useCallback((b: string) => { state.busy = b; emit(); }, []);
  const reload = useCallback(() => { state.reloadToken += 1; emit(); }, []);

  return {
    tab: state.tab, month: state.month, scenario: state.scenario,
    stage: state.stage, busy: state.busy, reloadToken: state.reloadToken,
    setTab, setMonth, setScenario, setStage, setBusy, reload,
  };
}
